import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  ChatBubbleLeftRightIcon,
  CpuChipIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const PRESETS = [
  { key: '6h', label: 'Last 6 hours', hours: 6, granularity: 'hour' },
  { key: '24h', label: 'Last 24 hours', hours: 24, granularity: 'hour' },
  { key: '7d', label: 'Last 7 days', hours: 24 * 7, granularity: 'day' },
  { key: '30d', label: 'Last 30 days', hours: 24 * 30, granularity: 'day' },
  { key: '12w', label: 'Last 12 weeks', hours: 24 * 7 * 12, granularity: 'week' }
];

const GRANULARITY_OPTIONS = [
  { key: 'hour', label: 'Per hour' },
  { key: 'day', label: 'Per day' },
  { key: 'week', label: 'Per week' }
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return '—';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const ChatbotHealthTab = () => {
  const { isDarkMode } = useTheme();

  const [activePreset, setActivePreset] = useState('7d');
  const [customRange, setCustomRange] = useState([null, null]);
  const [granularity, setGranularity] = useState('day');
  const [overview, setOverview] = useState(null);
  const [accountsData, setAccountsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('all'); // all | running | history
  const [accountsShown, setAccountsShown] = useState(25);
  const [customStart, customEnd] = customRange;

  const range = useMemo(() => {
    if (activePreset === 'custom' && customStart && customEnd) {
      return { from: customStart.toISOString(), to: customEnd.toISOString() };
    }
    const preset = PRESETS.find((p) => p.key === activePreset) || PRESETS[2];
    return {
      from: new Date(Date.now() - preset.hours * 3600 * 1000).toISOString(),
      to: new Date().toISOString()
    };
  }, [activePreset, customStart, customEnd]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { from: range.from, to: range.to, granularity };
      const [overviewRes, accountsRes] = await Promise.all([
        api.get('/chatbot-health/overview', { params }),
        api.get('/chatbot-health/accounts', { params: { from: range.from, to: range.to } })
      ]);
      setOverview(overviewRes.data);
      setAccountsData(accountsRes.data);
    } catch (err) {
      console.error('Error fetching chatbot health:', err);
      setError(err.response?.data?.error || 'Failed to load chatbot health data');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectPreset = (preset) => {
    setActivePreset(preset.key);
    setGranularity(preset.granularity);
  };

  const gridColor = isDarkMode ? 'rgba(148, 163, 184, 0.12)' : 'rgba(100, 116, 139, 0.15)';
  const tickColor = isDarkMode ? '#94a3b8' : '#64748b';

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        labels: { color: tickColor, usePointStyle: true, boxWidth: 8 }
      },
      tooltip: { padding: 10 }
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: tickColor, maxRotation: 45, autoSkip: true, maxTicksLimit: 16 }
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: { color: tickColor, precision: 0 }
      }
    }
  }), [gridColor, tickColor]);

  const labels = useMemo(
    () => overview?.replies?.series?.map((p) => p.bucket) || [],
    [overview]
  );

  const repliesChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Replies sent',
        data: overview?.replies?.series?.map((p) => p.sent) || [],
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 2
      },
      {
        label: 'Failed',
        data: overview?.replies?.series?.map((p) => p.failed) || [],
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 2
      }
    ]
  }), [overview, labels]);

  const aiChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Successful AI requests',
        data: overview?.aiRequests?.series?.map((p) => p.success) || [],
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderRadius: 4
      },
      {
        label: 'Failed AI requests',
        data: overview?.aiRequests?.series?.map((p) => p.failed) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4
      }
    ]
  }), [overview, labels]);

  const scansChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Successful scans',
        data: overview?.followBack?.scans?.series?.map((p) => p.success) || [],
        backgroundColor: 'rgba(14, 165, 233, 0.7)',
        borderRadius: 4
      },
      {
        label: 'Failed scans',
        data: overview?.followBack?.scans?.series?.map((p) => p.failed) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4
      }
    ]
  }), [overview, labels]);

  const dmsChart = useMemo(() => ({
    labels,
    datasets: [
      {
        label: 'Follow-back DMs sent',
        data: overview?.followBack?.dms?.series?.map((p) => p.sent) || [],
        backgroundColor: 'rgba(168, 85, 247, 0.7)',
        borderRadius: 4
      },
      {
        label: 'DM failures',
        data: overview?.followBack?.dms?.series?.map((p) => p.failed) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.7)',
        borderRadius: 4
      },
      {
        label: 'Follow-backs detected',
        data: overview?.followBack?.followBacksDetected?.series?.map((p) => p.count) || [],
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderRadius: 4
      }
    ]
  }), [overview, labels]);

  const filteredAccounts = useMemo(() => {
    let rows = accountsData?.accounts || [];
    if (accountFilter === 'running') rows = rows.filter((a) => a.running);
    if (accountFilter === 'history') rows = rows.filter((a) => !a.running);
    const q = accountSearch.trim().toLowerCase();
    if (q) rows = rows.filter((a) => (a.username || '').toLowerCase().includes(q));
    return rows;
  }, [accountsData, accountFilter, accountSearch]);

  const aiTotals = overview?.aiRequests?.totals || { success: 0, failed: 0 };
  const aiTotal = aiTotals.success + aiTotals.failed;
  const aiSuccessRate = aiTotal > 0 ? ((aiTotals.success / aiTotal) * 100).toFixed(1) : null;

  const statCards = [
    {
      label: 'Replies sent',
      value: overview?.replies?.totals?.sent ?? 0,
      sub: `${overview?.replies?.totals?.failed ?? 0} failed`,
      icon: ChatBubbleLeftRightIcon,
      color: 'text-emerald-500'
    },
    {
      label: 'AI request success',
      value: aiSuccessRate != null ? `${aiSuccessRate}%` : '—',
      sub: `${aiTotals.success} ok · ${aiTotals.failed} failed`,
      icon: CpuChipIcon,
      color: 'text-blue-500'
    },
    {
      label: 'Follow-back scans',
      value: overview?.followBack?.scans?.totals?.success ?? 0,
      sub: `${overview?.followBack?.scans?.totals?.failed ?? 0} failed`,
      icon: MagnifyingGlassIcon,
      color: 'text-sky-500'
    },
    {
      label: 'Follow-back DMs',
      value: overview?.followBack?.dms?.totals?.sent ?? 0,
      sub: `${overview?.followBack?.dms?.totals?.failed ?? 0} failed · ${overview?.followBack?.followBacksDetected?.total ?? 0} follow-backs`,
      icon: UserPlusIcon,
      color: 'text-purple-500'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="card overflow-visible">
        <div className="card-body overflow-visible space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time range
              </label>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    onClick={() => selectPreset(preset)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      activePreset === preset.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDarkMode
                          ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom range (date &amp; time)
              </label>
              <DatePicker
                selectsRange
                startDate={customStart}
                endDate={customEnd}
                onChange={(update) => {
                  setCustomRange(update);
                  if (update[0] && update[1]) setActivePreset('custom');
                }}
                showTimeSelect
                timeIntervals={60}
                dateFormat="MMM d, yyyy HH:mm"
                maxDate={new Date()}
                isClearable
                placeholderText="Pick start & end…"
                className={`px-3 py-1.5 text-sm rounded-lg border w-64 ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-200 border-slate-700'
                    : 'bg-white text-slate-700 border-slate-200'
                }`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Group by
              </label>
              <div className="flex gap-2">
                {GRANULARITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setGranularity(opt.key)}
                    className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                      granularity === opt.key
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDarkMode
                          ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={fetchData}
              disabled={loading}
              className={`ml-auto flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                isDarkMode
                  ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              } ${loading ? 'opacity-60' : ''}`}
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="card">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{card.sub}</p>
                </div>
                <card.icon className={`w-8 h-8 ${card.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Replies trend */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Chatbot replies over time
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Bot messages recorded per {granularity} — sent vs delivery failures
          </p>
          <div className="h-72">
            <Line data={repliesChart} options={chartOptions} />
          </div>
        </div>
      </div>

      {/* AI requests */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            AI response generation
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            LLM API requests per {granularity} — successful vs failed generations
          </p>
          <div className="h-72">
            <Bar data={aiChart} options={{ ...chartOptions, scales: { ...chartOptions.scales, x: { ...chartOptions.scales.x, stacked: true }, y: { ...chartOptions.scales.y, stacked: true } } }} />
          </div>
          {overview?.aiRequests?.recentErrors?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Latest AI request failures
              </h4>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {overview.aiRequests.recentErrors.map((e, i) => (
                  <div
                    key={i}
                    className={`text-xs px-3 py-2 rounded-lg ${
                      isDarkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
                    }`}
                  >
                    <span className="font-mono">{formatDateTime(e.at)}</span>
                    {e.provider ? ` · ${e.provider}` : ''} — {e.error || 'unknown error'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Follow-back scans + DMs */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Follow-back scans (AI VA agents)
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Follower scrapes per {granularity} — successful vs failed
            </p>
            <div className="h-64">
              <Bar data={scansChart} options={chartOptions} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              Follow-backs &amp; DMs
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Follow-backs detected and DMs sent/failed per {granularity}
            </p>
            <div className="h-64">
              <Bar data={dmsChart} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Accounts overview */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Chatbot accounts
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {accountsData?.totals?.running ?? 0} running now · {accountsData?.totals?.history ?? 0} previously ran
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { key: 'all', label: `All (${accountsData?.totals?.accounts ?? 0})` },
                { key: 'running', label: `Running (${accountsData?.totals?.running ?? 0})` },
                { key: 'history', label: `History (${accountsData?.totals?.history ?? 0})` }
              ].map((f) => (
                <button
                  key={f.key}
                  onClick={() => { setAccountFilter(f.key); setAccountsShown(25); }}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                    accountFilter === f.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : isDarkMode
                        ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <input
                type="text"
                value={accountSearch}
                onChange={(e) => { setAccountSearch(e.target.value); setAccountsShown(25); }}
                placeholder="Search username…"
                className={`px-3 py-1.5 text-sm rounded-lg border w-44 ${
                  isDarkMode
                    ? 'bg-slate-800 text-slate-200 border-slate-700 placeholder-slate-500'
                    : 'bg-white text-slate-700 border-slate-200 placeholder-slate-400'
                }`}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className={`text-left ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <th className="py-2 pr-4 font-medium">Account</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Latest inbox scan</th>
                  <th className="py-2 pr-4 font-medium">Latest reply</th>
                  <th className="py-2 pr-4 font-medium text-right">Replies (range)</th>
                  <th className="py-2 pr-4 font-medium text-right">Conversations</th>
                </tr>
              </thead>
              <tbody className={isDarkMode ? 'divide-y divide-slate-800' : 'divide-y divide-slate-100'}>
                {filteredAccounts.slice(0, accountsShown).map((a) => (
                  <tr key={a.accountId}>
                    <td className="py-2.5 pr-4 font-medium text-gray-900 dark:text-white">
                      @{a.username}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        a.running
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${a.running ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                        {a.running ? 'Running' : a.status || 'stopped'}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300" title={formatDateTime(a.lastInboxScanAt)}>
                      {timeAgo(a.lastInboxScanAt)}
                    </td>
                    <td className="py-2.5 pr-4 text-gray-600 dark:text-gray-300" title={formatDateTime(a.lastReplyAt)}>
                      {timeAgo(a.lastReplyAt)}
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                      {(a.repliesInRange || 0).toLocaleString()}
                    </td>
                    <td className="py-2.5 pr-4 text-right text-gray-900 dark:text-white">
                      {(a.totalConversations || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {filteredAccounts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-gray-500 dark:text-gray-400">
                      {loading ? 'Loading accounts…' : 'No chatbot accounts found for this filter.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filteredAccounts.length > accountsShown && (
            <div className="mt-3 text-center">
              <button
                onClick={() => setAccountsShown((n) => n + 50)}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Show more ({filteredAccounts.length - accountsShown} remaining)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatbotHealthTab;
